import { useState, useEffect, useCallback, useRef } from "react"
import { useRouter } from "next/router"

function usePortalModal(hashes, updateStateByHash = (hash) => null) {
  const ref = useRef()
  const router = useRouter()

  const [mounted, setMounted] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    ref.current = document.querySelector("#__next")
    setMounted(true)
  }, [])

  const path = router.asPath.toLowerCase().split("#")

  const closeModal = useCallback(() => {
    if (path[1] && hashes.includes(path[1])) {
      router.push(path[0], undefined, { shallow: true, scroll: false })
      setModalOpen(false)
      document.body.classList.remove("overflow-hidden")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router])

  const openModal = useCallback(
    (hash) => {
      document.body.classList.add("overflow-hidden")
      updateStateByHash(hash)
      setModalOpen(true)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router]
  )

  // for accessibility
  const focusRef = useCallback((modal) => {
    if (modal !== null) {
      modal.focus()
      modal.addEventListener("focusout", (e) => {
        if (!modal.contains(e.relatedTarget)) modal.focus()
      })
    }
  }, [])

  useEffect(() => {
    // open the modal regardless of whether user directly navigates to hash via address bar or viewport interaction e.g. button click
    if (path[1] && hashes.includes(path[1])) {
      openModal(path[1])
    }

    // determine whether or not to update state upon route navigation
    function handleHashChange(url) {
      const urlHash = url.toLowerCase().split("#")
      if (urlHash[1] && hashes.includes(urlHash[1])) {
        openModal(urlHash[1])
      } else {
        closeModal()
      }
    }
    router.events.on("routeChangeComplete", handleHashChange)

    // allow users to close modal with escape key
    function closeModalWithEsc(e) {
      if (e.key === "Escape") closeModal()
    }
    document.addEventListener("keyup", closeModalWithEsc)

    return () => {
      setModalOpen(false)
      router.events.off("routeChangeComplete", handleHashChange)
      document.removeEventListener("keyup", closeModalWithEsc)
      document.body.classList.remove("overflow-hidden")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router])

  return { ref, mounted, modalOpen, openModal, closeModal, focusRef }
}

export default usePortalModal
